import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { ComponentProps, JSXElementConstructor, useMemo } from 'react';

import { Switch } from '@/components/form/baseInputs/Switch';
import { Loader } from '@/components/progress/Loader/Loader';
import {
  DispositiveProvisionsContext,
  useDispositiveProvisionsContext,
} from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { useDispositiveProvisionsContextData } from '@/modules/dispositiveProvisions/hooks/useDispositiveProvisionsContextData';

import { HypotheticalWaterfallScenarioCallout } from '../components/HypotheticalWaterfallScenarioCallout';
import { BeneficiaryDesignationsListView } from './BeneficiaryDesignationsListView';
import { DispositiveProvisionsByDeath } from './DispositiveProvisionsByDeath';
import { DispositiveProvisionsForSingleClientHousehold } from './DispositiveProvisionsForSingleClientHousehold';
import { DispositiveProvisionsForTwoClientHousehold } from './DispositiveProvisionsForTwoClientHousehold';
import { useDispositiveProvisionsListViewSlots } from './hooks/useDispositiveProvisionsListViewSlots';
import { useDispositiveProvisionsListViewTab } from './hooks/useDispositiveProvisionsListViewTab';
import { NoDispositions } from './NoDispositions';

interface TrustDispositionsTabProps {
  currentNodeValue?: Decimal;
  firstDeathPrimaryClientId?: string | null;
}

function CalculateValuesSwitch() {
  const { showCalculatedDispositions, setShowCalculatedDispositions } =
    useDispositiveProvisionsContext();

  return (
    <Switch
      value={showCalculatedDispositions}
      onChange={() =>
        setShowCalculatedDispositions(!showCalculatedDispositions)
      }
      label="Show calculated distributions"
      labelPosition="right"
    />
  );
}

function DispositiveProvisionsListViewInner({
  currentNodeValue,
  firstDeathPrimaryClientId,
}: TrustDispositionsTabProps) {
  const {
    firstGrantorDeathId,
    dispositionScenarios,
    isTwoClientHousehold,
    isOnHypotheticalWaterfall,
    waterfall,
    entityType,
  } = useDispositiveProvisionsContext();
  const firstDeathId = firstDeathPrimaryClientId || firstGrantorDeathId;

  // Retirement / DH-insurance accounts use the tier-based BDG list instead
  // of the by-death disposition list.
  const useBeneficiaryDesignations = isBeneficiaryDesignationEntity(entityType);

  const activeTabProps = useDispositiveProvisionsListViewTab(firstDeathId);
  const { activePrimaryClientIdTab } = activeTabProps;

  if (dispositionScenarios && dispositionScenarios.length > 2) {
    throw new Error('Too many disposition scenarios. We only support 2.');
  }

  const dispositionScenarioForTab =
    dispositionScenarios?.find((scenario) => {
      return scenario.firstGrantorDeath.id === activePrimaryClientIdTab;
    }) ?? null;

  const hypotheticalWaterfallCallout =
    isOnHypotheticalWaterfall && waterfall ? (
      <HypotheticalWaterfallScenarioCallout
        waterfallName={waterfall.displayName}
      />
    ) : null;

  if (useBeneficiaryDesignations) {
    return (
      <Stack spacing={2}>
        {hypotheticalWaterfallCallout}
        {/* Calculated amounts derive from the waterfall edges, so only offer
            the toggle when a waterfall is in scope. */}
        {waterfall && <CalculateValuesSwitch />}
        <BeneficiaryDesignationsListView
          currentNodeValue={currentNodeValue}
          {...activeTabProps}
        />
      </Stack>
    );
  }

  if (waterfall) {
    return (
      <Stack spacing={2}>
        <CalculateValuesSwitch />
        <DispositiveProvisionsByDeath
          currentNodeValue={currentNodeValue}
          waterfall={waterfall}
          {...activeTabProps}
        />
      </Stack>
    );
  }

  if (dispositionScenarios?.length === 0) {
    return (
      <Stack spacing={2}>
        {hypotheticalWaterfallCallout}
        <NoDispositions
          currentNodeValue={currentNodeValue}
          {...activeTabProps}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {hypotheticalWaterfallCallout}
      {isTwoClientHousehold && (
        <DispositiveProvisionsForTwoClientHousehold
          dispositionScenarioForTab={dispositionScenarioForTab}
          currentNodeValue={currentNodeValue}
          {...activeTabProps}
        />
      )}
      {!isTwoClientHousehold && (
        <DispositiveProvisionsForSingleClientHousehold
          dispositionScenarioForTab={dispositionScenarioForTab}
          currentNodeValue={currentNodeValue}
          {...activeTabProps}
        />
      )}
    </Stack>
  );
}

export interface DispositiveProvisionsListViewProps {
  firstDeathPrimaryClientId?: string | null;
  currentNodeValue?: Decimal;
  slots?: {
    Loader?: JSXElementConstructor<ComponentProps<typeof Loader>>;
  };
}

export function DispositiveProvisionsListView(
  props: DispositiveProvisionsListViewProps
) {
  const dispositiveProvisionsData = useDispositiveProvisionsContextData();

  const slots = useDispositiveProvisionsListViewSlots(props.slots);

  const isLoading = useMemo(() => {
    return dispositiveProvisionsData.loading;
  }, [dispositiveProvisionsData.loading]);

  if (isLoading) {
    return (
      <slots.Loader
        boxProps={{
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          },
        }}
      />
    );
  }

  return (
    <DispositiveProvisionsContext.Provider
      value={
        {
          ...dispositiveProvisionsData,
          formVariant: 'editEntity',
        } as DispositiveProvisionsContext
      }
    >
      <DispositiveProvisionsListViewInner {...props} />
    </DispositiveProvisionsContext.Provider>
  );
}
